import { ATTENDANCE_RULES } from '@/constant/hr/attendance-rule-catalog';
import { HRM_PERMISSION_CATALOG, HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { blocksClient } from '@/lib/blocks/client';
import { getIamUserOrganizationAccess } from './employee-account.service';

/**
 * Whether lunch / overtime / breaks are on for an employee. This is decided by Blocks IAM, not by a
 * profile flag or the employee's region: it's on when the employee holds `HRM_PERMISSIONS.PROFILE.
 * LUNCH_ENABLED` / `OVERTIME_ENABLED` / one of `BREAK_PERMISSIONS` through one of their roles. The
 * signed-in user's own permissions come from the token; for anybody else they are resolved here
 * from the two sides IAM exposes — the roles each permission is assigned to, and the roles each
 * user holds.
 */
export interface WorkStatusEligibility {
  lunch: boolean;
  overtime: boolean;
  breaks: boolean;
}

export interface EligibilityRoles {
  lunch: string[];
  overtime: string[];
  breaks: string[];
}

/** The permissions that let an employee take a break: regular breaks, or any lunch rule. */
export const BREAK_PERMISSIONS = [
  HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_TAKE_BREAK,
  ...ATTENDANCE_RULES.filter((rule) => rule.group === 'lunch').map((rule) => rule.permission),
];

export type IamPermission = { resource?: string; roles?: unknown; isArchived?: boolean };

export const stringsOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * The IAM resource groups of the given permissions, read from the catalog (one group per area or
 * subject, e.g. `people`, `time-attendance-rule`), so a lookup follows the catalog when it is regrouped.
 */
export const resourceGroupsOf = (resources: readonly string[]): string[] => [
  ...new Set(
    HRM_PERMISSION_CATALOG.filter((permission) => resources.includes(permission.resource)).map(
      (permission) => permission.resourceGroup
    )
  ),
];

/**
 * The live (not archived) IAM records of the given permissions, each listing the roles it is assigned
 * to. IAM filters by one resource group per call, so the groups are read side by side.
 */
export const listIamPermissions = async (resources: readonly string[]): Promise<IamPermission[]> => {
  const pages = await Promise.all(
    resourceGroupsOf(resources).map(
      (resourceGroup) =>
        blocksClient.iam.permissions.list({
          filter: { resourceGroup },
          page: 0,
          pageSize: 100,
        }) as Promise<{ data?: IamPermission[] } | undefined>
    )
  );
  return pages
    .flatMap((page) => page?.data ?? [])
    .filter(
      (permission) =>
        !permission.isArchived && Boolean(permission.resource) && resources.includes(permission.resource!)
    );
};

/** The roles each permission is assigned to (an IAM permission lists its roles). */
export const getEligibilityRoles = async (): Promise<EligibilityRoles> => {
  const permissions = await listIamPermissions([
    HRM_PERMISSIONS.PEOPLE.LUNCH_ENABLED,
    HRM_PERMISSIONS.PEOPLE.OVERTIME_ENABLED,
    ...BREAK_PERMISSIONS,
  ]);
  const rolesOf = (resource: string) =>
    stringsOf(permissions.find((permission) => permission.resource === resource)?.roles);
  return {
    lunch: rolesOf(HRM_PERMISSIONS.PEOPLE.LUNCH_ENABLED),
    overtime: rolesOf(HRM_PERMISSIONS.PEOPLE.OVERTIME_ENABLED),
    breaks: [...new Set(BREAK_PERMISSIONS.flatMap(rolesOf))],
  };
};

/** The user's roles in the given organization, or in every organization when it isn't known. */
export const getUserRolesInOrganization = async (
  userId: string,
  organizationId?: string
): Promise<string[]> => {
  const { organizationAccess } = await getIamUserOrganizationAccess(userId);
  const current = organizationId
    ? organizationAccess.find((entry) => entry.organizationId === organizationId)
    : undefined;
  return current ? current.roles : [...new Set(organizationAccess.flatMap((entry) => entry.roles))];
};

export const resolveWorkStatusEligibility = (
  userRoles: readonly string[],
  eligibilityRoles: EligibilityRoles
): WorkStatusEligibility => ({
  lunch: userRoles.some((role) => eligibilityRoles.lunch.includes(role)),
  overtime: userRoles.some((role) => eligibilityRoles.overtime.includes(role)),
  breaks: userRoles.some((role) => eligibilityRoles.breaks.includes(role)),
});
